import { useEffect } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Wire the Capacitor `App` plugin's back-button event to React Router.
 *
 * Behaviour matches the Android TV / mobile expectation:
 *   - If the app is deeper than the discover root, go back one route.
 *   - If a player is fullscreen, just leave fullscreen.
 *   - If we're at the discover root, exit the app (Android hardware
 *     back closes it; TV Home key already handles the same on its own).
 *
 * No-op on non-native platforms (browser back button already works
 * without us doing anything).
 */
export function useBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const promise = App.addListener("backButton", ({ canGoBack }) => {
      // A fullscreen player eats the first Back — leaving the screen
      // from fullscreen would be a surprise on a remote.
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => {});
      } else if (canGoBack && location.pathname !== "/discover") {
        navigate(-1);
      } else {
        void App.exitApp();
      }
    });

    return () => {
      // Capacitor 6's addListener is thenable — clean up when it lands.
      void promise.then((handle) => handle.remove());
    };
  }, [navigate, location.pathname]);
}
